import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, Eye, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const inputCls = "w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 pr-11 text-slate-100 focus:border-cyan-400 focus:outline-none";

function PwField({ label, testId, value, onChange, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="text-sm text-slate-300 mb-1.5 block">{label}</label>
      <div className="relative">
        <input data-testid={testId} type={show ? "text" : "password"} value={value} onChange={onChange} autoComplete={autoComplete} required className={inputCls} placeholder="••••••••" />
        <button type="button" onClick={() => setShow(!show)} data-testid={`${testId}-toggle`} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-cyan-300" aria-label="Toggle visibility">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

const strength = (p) => {
  let s = 0;
  if (p.length >= 8) s++; if (p.length >= 12) s++; if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++; if (/\d/.test(p)) s++; if (/[^A-Za-z0-9]/.test(p)) s++;
  return s;
};
const strengthLabel = ["", "Weak", "Fair", "Good", "Strong", "Very strong"];
const strengthCls = ["", "bg-red-500", "bg-amber-500", "bg-yellow-400", "bg-emerald-400", "bg-cyan-400"];

export function ChangePasswordCard({ className = "", subtitle = "Choose a strong password you don't use anywhere else." }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const s = strength(next);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) return toast.error("New passwords do not match");
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next, confirm_password: confirm });
      toast.success("Password changed successfully");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className={`glass rounded-2xl p-6 ${className}`} data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-1"><KeyRound className="w-5 h-5 text-cyan-400" /><h3 className="font-heading text-white">Change Password</h3></div>
      <p className="text-sm text-slate-500 mb-5">{subtitle}</p>
      <form onSubmit={submit} className="space-y-4">
        <PwField label="Current password" testId="pw-current" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
        <div className="grid sm:grid-cols-2 gap-4">
          <PwField label="New password" testId="pw-new" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
          <PwField label="Confirm new password" testId="pw-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
        </div>
        {next && (
          <div data-testid="pw-strength">
            <div className="flex gap-1 mb-1">{[1, 2, 3, 4, 5].map((i) => <div key={i} className={`h-1 flex-1 rounded-full ${i <= s ? strengthCls[s] : "bg-slate-800"}`} />)}</div>
            <p className="text-xs text-slate-500">{strengthLabel[s]}{confirm && next !== confirm && <span className="text-red-400 ml-2">· passwords don't match</span>}</p>
          </div>
        )}
        <button type="submit" disabled={loading || !current || !next || !confirm} data-testid="pw-submit" className="btn-glow px-6 py-2.5 rounded-lg font-semibold text-slate-950 inline-flex items-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Update Password
        </button>
      </form>
    </div>
  );
}
